import type { CardType } from '../payments';

export interface Shift4SaleResponse {
  result: SaleResult[];
}

export interface SaleResult {
  dateTime: string;
  receiptColumns: number;
  card: Card;
  clerk: Clerk;
  customer: Customer;
  device: Device;
  merchant: Merchant;
  receipt: Receipt[];
  server: Server;
  transaction: Transaction;
  universalToken: Token;
}

export interface SanitizedPaymentResult {
  customer?: Customer;
  card?: {
    type?: string;
    entryMode?: string;
    present?: boolean;
  };
  receipt?: Array<{
    key: string;
    value: string;
  }>;
  transaction?: {
    purchaseCard?: unknown;
    universalToken?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}
interface RefundResult {
  amount: {
    total: number;
    tax: number;
  };
  card: {
    entryMode: string;
    expirationDate: number;
    levelResult: string;
    number: string;
    present: string;
    type: string;
    balance: {
      amount: number;
    };
    token: {
      value: string;
    };
  };
  customer: {
    firstName: string;
    lastName: string;
  };
  transaction: {
    invoice: string;
  };
}

interface Transaction {
  authorizationCode: string;
  authSource: string;
  avs?: Avs;
  invoice: string;
  purchaseCard: PurchaseCard;
  responseCode: string;
  saleFlag: string;
}

interface PurchaseCard {
  customerReference: string;
  destinationPostalCode: string;
  productDescriptors: string[];
}

interface Avs {
  result: string;
  postalCodeVerified: string;
  streetVerified: string;
  valid: string;
}

interface Server {
  name: string;
}

interface Receipt {
  key: string;
  printValue: string;
  printName?: string;
}

interface Merchant {
  mid: number;
}

interface Device {
  capability: Capability;
}

interface Capability {
  magstripe: string;
  manualEntry: string;
}

interface Customer {
  addressLine1: string;
  firstName: string;
  lastName: string;
  postalCode: string;
}

interface Clerk {
  numericId: number;
}

interface Card {
  type: CardType;
  entryMode: string;
  number: string;
  present: string;
  securityCode: {
    result: string;
  };
  token: Token;
}

interface Token {
  value: string;
}

enum CardErrorKey {
  RESPONSE_CODES = 'RESPONSE_CODES',
  AVS = 'AVS',
  CVV = 'CVV',
}
export enum ERROR_KEY {
  A = 'A',
  D = 'D',
  E = 'E',
  N = 'N',
  P = 'P',
  R = 'R',
  S = 'S',
  U = 'U',
  X = 'X',
  Z = 'Z',
}
export const CardError: {
  [key in CardErrorKey]: { [key in ERROR_KEY]?: string };
} = {
  // Response codes
  RESPONSE_CODES: {
    D: 'Declined',
  },

  // AVS
  AVS: {
    A: 'ZIP/Postal Code Mismatch',
    E: 'AVS Error',
    N: 'Address (Street) or ZIP mismatch',
    R: 'Retry - System unavailable or timed out',
    S: 'Service not supported by issuer',
    U: 'Address information unavailable',
    X: 'Address mismatch (9 digit zip code)',
    Z: 'Address mismatch (5 digit zip code)',
  },

  // CVV
  CVV: {
    N: 'CVV Mismatch',
    P: 'CVV Not processed',
    S: 'CVV Should have been present',
    U: 'CVV Issuer unable to process request',
  },
};

export const ResponseCodes = {
  A: { type: 'Approved', text: 'The transaction is approved.' },
  C: {
    type: 'Approved',
    text: 'The transaction is approved without requiring additional authorization because it is less than or equal to a ceiling amount. (The ceiling amount is the original authorization amount multiplied by the tolerance per the merchant’s settings with Shift4 Payments.)',
  },
  D: {
    type: 'Declined',
    text: 'The transaction has been declined.',
  },
  e: { type: 'Error', text: 'There is an error condition.' },
  f: {
    type: 'AVS or CSC failure',
    text: 'An AVS or CSC failure has occurred.',
  },
  P: {
    type: 'Partial',
    text: 'A partial approval has occurred. Check amount.total for the approved amount.',
  },
  R: {
    type: 'Voice',
    text: 'The transaction requires a voice referral.',
  },
  X: {
    type: 'Expired',
    text: "The credit card used for this transaction has passed it's expiration date.",
  },
  S: {
    type: 'SCA',
    text: 'Online PIN required. The contactless EMV transaction requires strong customer authentication to continue. The terminal must gather the online PIN if supported by the device form factor and CVM list then resubmit the transaction request.',
  },
  I: {
    type: 'SCA',
    text: 'Interface switch required. The contactless EMV transaction requires strong customer authentication to continue. The terminal must look at the form factor indicator to determine if the transaction should be declined, switched to EMV contact or tapped again using CDCVM.',
  },
};

export type ResponseCode = keyof typeof ResponseCodes;

export interface Shift4RefundResponse {
  result: RefundResult[];
}
